import { FC, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { PreloaderCircular } from '@shared/ui/kit';
import { FormCanvas, FormData, Form, useFetchFormByLink, FormStatusEnum } from '@entities/leadCollector';
import { useFormValidation } from './lib/useFormValidation';
import { useFormSubmission } from './lib/useFormSubmission';
import { FormErrorLayout } from './ui/FormErrorLayout';
import { FormErrorInvite } from './ui/FormErrorInvite';
import { FormUsedFormWrapper } from './ui/FormUsedFormWrapper';

export const FormPage: FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const { form, formData: initialFormData, fetching, error } = useFetchFormByLink(hash ?? '');
  const [formData, setFormData] = useState<FormData | undefined>(initialFormData);
  const { validateForm } = useFormValidation();
  const { submissionResponse, handleSubmitForm } = useFormSubmission(hash, formData);

  useEffect(() => {
    if (!initialFormData) return;
    setFormData(structuredClone(initialFormData));
  }, [initialFormData]);

  const onSaveForm = async () => {
    if (!formData) return;

    const isValid = await validateForm(formData);
    setFormData({ ...formData });

    if (!isValid) return;

    await handleSubmitForm();
  };

  const onChangeProperties = (instanceId: string, properties: Record<string, unknown>) => {
    setFormData((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        components: prev.components.map((component) =>
          component.instanceId === instanceId
            ? { ...component, properties: { ...component.properties, ...properties } }
            : component
        ),
      };
    });
  };

  if (fetching) {
    return (
      <div {...stylex.props(styles.loader)}>
        <PreloaderCircular size="large" />
      </div>
    );
  }

  if (error || !form) {
    return <FormErrorLayout />;
  }

  if (form.formLinkStatus === FormStatusEnum.EXPIRED) {
    return <FormErrorInvite />;
  }

  if (form.formLinkStatus === FormStatusEnum.USED) {
    return <FormUsedFormWrapper eventName={form.eventName} dateStart={form.dateStart} dateEnd={form.dateEnd} />;
  }

  if (!formData) {
    return <FormErrorInvite />;
  }

  if (submissionResponse?.submissionFormStatus === FormStatusEnum.EXPIRED) {
    return <FormErrorInvite />;
  }

  if (submissionResponse?.submissionFormStatus === FormStatusEnum.USED) {
    return (
      <FormUsedFormWrapper
        eventName={submissionResponse.eventName}
        dateStart={submissionResponse.dateStart}
        dateEnd={submissionResponse.dateEnd}
      />
    );
  }

  if (!formData) {
    return (
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.errorContent)}>
          <p {...stylex.props(styles.errorText)}>Ошибка загрузки формы</p>
        </div>
      </div>
    );
  }

  return (
    <div {...stylex.props(styles.container)}>
      <FormCanvas
        formData={formData}
        form={
          {
            maxExtraAdults: form.maxExtraAdults,
            maxExtraChildren: form.maxExtraChildren,
          } as Form
        }
        onChangeProperties={onChangeProperties}
        isCanEdit={true}
        onSaveForm={onSaveForm}
        onDeleteComponent={undefined}
        onReorder={undefined}
        isLoadingForm={false}
      />
    </div>
  );
};

export default FormPage;
